import React from 'react';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

export type ActivityView = 'explorer' | 'history';

type Props = {
  /** 現在アクティブなビュー（サイドバー非表示時は null） */
  active: ActivityView | null;
  onSelect: (view: ActivityView) => void;
  /** 管理者（ADMIN/DEVELOPER）用のユーザー切替を表示するか */
  showAdmin?: boolean;
  /** 他ユーザーのワークスペースを閲覧中か（編集ロックの状態表示に使用） */
  isOtherWorkspace?: boolean;
  /** 編集可能か（他ユーザー閲覧時のロック解除状態） */
  editable?: boolean;
  onOpenUserSwitch?: (anchor: HTMLElement) => void;
  onToggleEditable?: () => void;
};

/** バーのアイコン共通スタイル */
const iconBoxSx = (isActive: boolean) => ({
  position: 'relative' as const,
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: 1,
  color: isActive ? 'primary.main' : 'text.secondary',
  transition: 'background-color 0.2s',
  '&:hover': { bgcolor: 'action.selected' },
  '&::before': isActive
    ? {
        content: '""',
        position: 'absolute' as const,
        left: -4,
        top: 8,
        bottom: 8,
        width: 2,
        bgcolor: 'primary.main',
        borderRadius: 1,
      }
    : undefined,
});

/**
 * 最左の固定アクティビティバー。
 * エクスプローラー / 実行履歴 の切替。選択中アイコン再クリックでサイドバーをトグル。
 * 管理者はバー下部からユーザー切替・編集ロックの操作ができる。
 */
const ActivityBar: React.FC<Props> = ({
  active,
  onSelect,
  showAdmin,
  isOtherWorkspace,
  editable,
  onOpenUserSwitch,
  onToggleEditable,
}) => {
  const items: { view: ActivityView; icon: React.ReactNode; label: string }[] = [
    { view: 'explorer', icon: <FolderIcon />, label: 'エクスプローラー' },
    { view: 'history', icon: <HistoryIcon />, label: '実行履歴' },
  ];

  return (
    <Stack
      sx={{
        width: 48,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        py: 1,
        alignItems: 'center',
        bgcolor: 'action.hover',
      }}
    >
      <Stack sx={{ alignItems: 'center', gap: 0.5 }}>
        {items.map((item) => {
          const isActive = active === item.view;
          return (
            <Tooltip key={item.view} title={item.label} placement="right">
              <Box onClick={() => onSelect(item.view)} sx={iconBoxSx(isActive)}>
                {React.cloneElement(item.icon as React.ReactElement<{ sx?: object }>, {
                  sx: { fontSize: 22 },
                })}
              </Box>
            </Tooltip>
          );
        })}
      </Stack>

      {/* 管理者操作（バー下部） */}
      {showAdmin && (
        <Stack sx={{ alignItems: 'center', gap: 0.5, mt: 'auto' }}>
          {isOtherWorkspace && (
            <Tooltip title={editable ? '閲覧のみに戻す' : '編集可能にする'} placement="right">
              <Box
                onClick={onToggleEditable}
                sx={{
                  ...iconBoxSx(Boolean(editable)),
                  color: editable ? 'primary.main' : 'text.secondary',
                }}
              >
                {editable ? (
                  <EditIcon sx={{ fontSize: 20 }} />
                ) : (
                  <EditOffIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
            </Tooltip>
          )}
          <Tooltip title="ユーザーのワークスペースを開く" placement="right">
            <Box onClick={(e) => onOpenUserSwitch?.(e.currentTarget)} sx={iconBoxSx(false)}>
              <GroupIcon sx={{ fontSize: 22 }} />
            </Box>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};

export default ActivityBar;
